import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges, AfterViewInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FileWithName, StrapiButtonLink } from '../../interfaces/cpe-interfaces.index';
import { FileService } from '../../services/file.service';
import { ImagesUtil } from '@cpe/app/utils/image.util';

@Component({
  standalone: true,
  selector: 'app-button-link',
  templateUrl: './button-link.component.html',
  styleUrls: ['./button-link.component.scss'],
  imports: [CommonModule, RouterModule],
  providers: [ImagesUtil],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ButtonLinkComponent implements OnInit, OnChanges, AfterViewInit {

  private readonly router = inject(Router);
  private readonly fileService = inject(FileService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['strapiButtonLink'] && this.strapiButtonLink && this.buttonActionType === 'link') {
      this.initComponent(this.strapiButtonLink);
    }
  }
  readonly imageUtils = inject(ImagesUtil);

  @Input() id?: string;
  @Input() ariaLabel?: string;
  @Input() altAriaLabel?: string;
  @Input() iconName?: string;
  @Input() iconSvg?: string;
  @Input() label?: string;
  @Input() size?: 'small' | 'standard' = 'standard';
  @Input() shape?: 'rounded' | 'squared' = 'rounded';
  @Input() type?: 'label' | 'icon-left' | 'icon-right' | 'icon-only' = 'label';
  @Input() variant?: 'primary' | 'secondary' | 'tertiary' | 'header' = 'primary';
  @Input() theme?: 'light' | 'dark' | 'neutral' = 'light';
  @Input() color?: 'basic' | 'danger' | 'input' = 'basic';
  @Input() textWeight?: 'button-light' | 'regular' | 'medium' | 'bold' | 'italic' = 'regular';
  @Input() materialIconSize?: 'msmall' | 'mstandard' = 'mstandard';
  @Input() buttonActionType?: 'download' | 'link' = 'link';
  @Input() file?: FileWithName;
  @Input() fullWidth = false;
  @Input() role?: 'button' | 'link' = 'link';
  @Input() url!: string;
  @Input() externalLink = false;

  @Input() strapiButtonLink?: StrapiButtonLink;
  @Output() focusEvent: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  getClassObject() {
    return {
      [this.size ?? 'standard']: !!this.size,
      [this.type ?? 'label']: !!this.type,
      [this.variant ?? 'primary']: !!this.variant,
      [this.color ?? 'basic']: !!this.color,
      [this.theme ?? 'light']: !!this.theme,
      [this.shape ?? 'rounded']: !!this.shape,
      [this.textWeight ?? 'regular']: !!this.textWeight,
    };
  }

  getIconClassObject() {
    return {
      [this.materialIconSize ?? 'mstandard']: !!this.materialIconSize,
    };
  }

  getTextAlign(): string {
    return this.type === 'icon-left' ? 'row' : 'row-reverse';
  }

  onKeyUpLink(event: KeyboardEvent) {
    const pressedKey = event.key;

    if (pressedKey === ' ') {
      this.router.navigate([this.url]);
    }
  }

  ngOnInit(): void {
    if (this.buttonActionType === 'download') {
      this.iconName = 'download';
      this.label = this.file?.name;
    }
  }

  ngAfterViewInit(): void {
    if (this.strapiButtonLink && this.buttonActionType === 'link') {
      this.initComponent(this.strapiButtonLink);
    }
  }

  initComponent(strapiButtonLink: StrapiButtonLink) {
    this.label = strapiButtonLink.name ?? '';
    this.ariaLabel =
      strapiButtonLink.ariaLabel ??
      (this.altAriaLabel ? `${this.label} ${this.altAriaLabel}` : undefined);
    this.url = strapiButtonLink.url ?? '';
    this.externalLink = strapiButtonLink.isExternal;
    if (this.externalLink) {
      this.iconName = 'arrow_outward';
    }
  }

  onKeyUpDownload(event: KeyboardEvent) {
    const pressedKey = event.key;

    if (pressedKey === ' ') {
      this.downloadFile();
    }
  }

  downloadClickEvent(event: MouseEvent) {
    event.preventDefault();
    this.downloadFile();
  }

  getUrlFile(urlPath: string): string {
    return this.imageUtils.getImageUrl(urlPath);
  }

  downloadFile() {
    if (this.file) {
      this.fileService.downloadPdf(this.file.file.url).subscribe((blob) => {
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = this.file?.name ?? 'archivo.pdf';

        link.click();
        URL.revokeObjectURL(objectUrl); // clean memory
      });
    }
  }
}
